import { Link, useParams } from "react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { blogPosts, getBlogPostBySlug } from "../blog/posts";
import { getSiteOrigin } from "../site";

function formatDate(date: string) {
  return new Date(date).toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getImageCreditLabel(url: string) {
  return url.includes("unsplash.com") ? " / Unsplash" : "";
}

export function BlogPost() {
  const { slug = "" } = useParams();
  const post = getBlogPostBySlug(slug);

  if (!post) {
    return (
      <div className="bg-stone-50 pt-20 text-gray-950">
        <Seo
          title="Beitrag nicht gefunden"
          description="Der angeforderte Blogbeitrag wurde nicht gefunden."
          path={`/blog/${slug}`}
          noIndex
        />

        <section className="mx-auto max-w-4xl px-4 py-24 text-center sm:px-6 lg:px-8">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-gray-500">Blog</p>
          <h1 className="mt-4 text-4xl leading-tight">Beitrag nicht gefunden</h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-gray-700">
            Die URL existiert nicht oder der Beitrag wurde verschoben.
          </p>
          <Link
            to="/blog"
            className="mt-8 inline-flex items-center gap-2 border border-black px-6 py-3 text-sm font-medium text-black transition-colors hover:bg-black hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zum Blog
          </Link>
        </section>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter((entry) => entry.slug !== post.slug)
    .sort((left, right) => {
      const leftScore = left.category === post.category ? 1 : 0;
      const rightScore = right.category === post.category ? 1 : 0;

      if (leftScore !== rightScore) {
        return rightScore - leftScore;
      }

      return right.publishedAt.localeCompare(left.publishedAt);
    })
    .slice(0, 2);
  const canonicalPath = `/blog/${post.slug}`;
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      articleSection: post.category,
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      url: `${getSiteOrigin()}${canonicalPath}`,
      image: post.imageUrl,
      keywords: post.keywords?.join(", "),
      author: {
        "@type": "Organization",
        name: "mutzur",
      },
      publisher: {
        "@type": "Organization",
        name: "mutzur",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Start",
          item: `${getSiteOrigin()}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: `${getSiteOrigin()}/blog`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: post.title,
          item: `${getSiteOrigin()}${canonicalPath}`,
        },
      ],
    },
    post.faq?.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faq.map((entry) => ({
            "@type": "Question",
            name: entry.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: entry.answer,
            },
          })),
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="bg-stone-50 pt-20 text-gray-950">
      <Seo
        title={post.title}
        description={post.description}
        path={canonicalPath}
        type="article"
        image={post.imageUrl}
        imageAlt={post.imageAlt}
        publishedTime={post.publishedAt}
        keywords={
          post.keywords ?? [post.category.toLowerCase(), post.slug.replace(/-/g, " "), "mutzur blog"]
        }
        structuredData={structuredData}
      />

      <section className="py-12 lg:py-16">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr),320px] lg:px-8">
          <article className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8 lg:p-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-black"
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück zum Blog
            </Link>

            <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-gray-500">
              <span className="rounded-full bg-stone-100 px-3 py-1 font-medium text-gray-700">
                {post.category}
              </span>
              <span>{formatDate(post.publishedAt)}</span>
              <span>{post.readingTime}</span>
            </div>

            <h1 className="mt-5 text-4xl leading-tight lg:text-5xl">{post.title}</h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-gray-700 sm:text-lg">
              {post.description}
            </p>

            <figure className="mt-10 overflow-hidden rounded-3xl border border-stone-200 bg-stone-50">
              <img
                src={post.imageUrl}
                alt={post.imageAlt}
                className="aspect-[16/8] w-full object-cover"
              />
              <figcaption className="px-5 py-4 text-sm text-gray-500">
                Foto:{" "}
                <a
                  href={post.imageCreditUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-stone-300 underline-offset-4"
                >
                  {post.imageCreditName}
                  {getImageCreditLabel(post.imageCreditUrl)}
                </a>
              </figcaption>
            </figure>

            <p className="mt-10 text-lg leading-relaxed text-gray-700">{post.excerpt}</p>

            <div className="mt-10 space-y-10">
              {post.sections.map((section) => (
                <section key={section.heading} className="space-y-4">
                  <h2 className="text-2xl leading-tight">{section.heading}</h2>
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="text-base leading-relaxed text-gray-700">
                      {paragraph}
                    </p>
                  ))}
                </section>
              ))}
            </div>

            {post.faq?.length ? (
              <section className="mt-12 space-y-6 border-t border-stone-200 pt-10">
                <h2 className="text-2xl leading-tight">Häufige Fragen</h2>
                <div className="space-y-5">
                  {post.faq.map((entry) => (
                    <div key={entry.question} className="rounded-2xl border border-stone-200 p-5">
                      <h3 className="text-lg leading-snug text-gray-950">{entry.question}</h3>
                      <p className="mt-3 text-base leading-relaxed text-gray-700">{entry.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
          </article>

          <aside className="space-y-6">
            <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg">Kernaussagen</h2>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-gray-700">
                {post.highlights.map((highlight) => (
                  <li key={highlight} className="border-b border-stone-100 pb-3 last:border-b-0 last:pb-0">
                    {highlight}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-stone-200 bg-black p-6 text-white shadow-sm">
              <p className="text-sm uppercase tracking-[0.18em] text-white/60">Nächster Schritt</p>
              <h2 className="mt-3 text-2xl leading-tight">
                Wenn die Website klar kalkulierbar bleiben soll
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-white/75">
                Über den Fragebogen lässt sich schnell klären, ob das Modell zu Ihrem Unternehmen
                und Ihrem Umfang passt.
              </p>
              <Link
                to="/fragebogen"
                className="mt-6 inline-flex items-center gap-2 border border-white/30 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white hover:text-black"
              >
                Fragebogen starten
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg">Weitere Beiträge</h2>
              <div className="mt-4 space-y-4">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="block rounded-2xl border border-stone-200 p-4 transition-colors hover:border-stone-300 hover:bg-stone-50"
                  >
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-gray-500">
                      {relatedPost.category}
                    </p>
                    <p className="mt-2 text-base leading-snug text-gray-950">{relatedPost.title}</p>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
